/**
 * <MatchedBuyersPanel leadId={id} />
 *
 * Surfaces saved buyers from /app/buyers that match this lead's buy-box.
 * Match logic lives server-side at GET /api/buyer-search/match/{lead_id}.
 *
 * Scoring weights:  market 40 / price 30 / beds 20 / activity 10  (max 100).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Users, ChevronRight, Star, MapPin, ArrowRight, ExternalLink } from "lucide-react";
import { api, fmtMoney } from "@/lib/api";

export default function MatchedBuyersPanel({ leadId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!leadId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/buyer-search/match/${leadId}`);
        setData(data);
      } catch {
        setData(null);
      }
      setLoading(false);
    })();
  }, [leadId]);

  if (loading) {
    return (
      <div className="card p-4 text-center text-sm text-[var(--ink-3)]" data-testid="matched-buyers-loading">
        <Loader2 size={14} className="animate-spin inline mr-2" />
        Finding buyers in your list that match this lead…
      </div>
    );
  }
  if (!data) return null;
  const matches = data.matches || [];
  const total = data.total_buyers_in_list || 0;

  return (
    <section className="card p-5" data-testid="matched-buyers-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
            Auto-Match
          </div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Sparkles size={16} className="text-emerald-600" />
            Buyers matching this deal
          </h3>
        </div>
        <Link
          to="/v3/crm/buyers/search"
          className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
          data-testid="matched-buyers-find-more"
        >
          Find more buyers <ExternalLink size={11} />
        </Link>
      </div>

      {matches.length === 0 ? (
        <div className="text-center py-6 text-sm text-[var(--ink-3)]" data-testid="matched-buyers-empty">
          <Users size={28} className="mx-auto opacity-40 mb-2" />
          {total === 0 ? (
            <>
              <p>You don&apos;t have any buyers saved yet.</p>
              <Link to="/v3/crm/buyers/search" className="text-emerald-700 font-bold hover:underline">
                Search cash buyers in your market →
              </Link>
            </>
          ) : (
            <p>None of your <strong>{total}</strong> saved buyer{total === 1 ? "" : "s"} match this deal&apos;s buy-box. Try widening your buyer list.</p>
          )}
        </div>
      ) : (
        <>
          <div className="text-xs text-[var(--ink-3)] mb-3">
            <strong className="text-[var(--ink)]">{matches.length}</strong> of your <strong className="text-[var(--ink)]">{total}</strong> saved buyer{total === 1 ? "" : "s"} could close this deal.
          </div>
          <div className="space-y-2">
            {matches.slice(0, 5).map((m) => (
              <MatchRow key={m.id} buyer={m} />
            ))}
            {matches.length > 5 && (
              <Link
                to="/v3/crm/buyers"
                className="block text-center text-xs text-emerald-700 hover:underline py-2"
              >
                See all {matches.length} matches →
              </Link>
            )}
          </div>
        </>
      )}
    </section>
  );
}


function MatchRow({ buyer }) {
  const scoreColor =
    buyer.match_score >= 80 ? "bg-emerald-500 text-white"
    : buyer.match_score >= 60 ? "bg-amber-400 text-amber-900"
    : "bg-[var(--bg-2)] text-[var(--ink-2)]";
  return (
    <div
      className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-[var(--line)] bg-white hover:bg-[var(--bg-2)] transition-colors"
      data-testid={`match-row-${buyer.id}`}
    >
      <div className={`shrink-0 rounded font-display font-black text-sm w-10 h-10 flex items-center justify-center ${scoreColor}`} title="Match score">
        {buyer.match_score}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm truncate">{buyer.name}</span>
          {buyer.entity_type && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--bg-2)] text-[var(--ink-3)]">
              {buyer.entity_type}
            </span>
          )}
        </div>
        {buyer.primary_market && (
          <div className="text-[11px] text-[var(--ink-3)] flex items-center gap-1 mt-0.5">
            <MapPin size={10} /> {buyer.primary_market}
            {buyer.properties_owned > 0 && (
              <span> · {buyer.properties_owned} properties</span>
            )}
            {buyer.avg_purchase_price_cents > 0 && (
              <span> · {fmtMoney(buyer.avg_purchase_price_cents)} avg</span>
            )}
          </div>
        )}
        {buyer.match_reasons?.length > 0 && (
          <div className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1 flex-wrap">
            {buyer.match_reasons.map((r, i) => (
              <span key={i} className="inline-flex items-center gap-0.5">
                <Star size={8} className="fill-current" /> {r}
              </span>
            ))}
          </div>
        )}
      </div>
      <ChevronRight size={14} className="text-[var(--ink-3)] shrink-0 mt-2" />
    </div>
  );
}
